// src/pages/Shopify.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { SkeletonShopify } from '../components/ui/Skeleton';
import {
  Store, Plus, Settings, Trash2, RefreshCw, Package, Search,
  Save, ExternalLink, Check, X, ChevronDown, ChevronUp,
} from 'lucide-react';

const API_BASE = '/api/shopify';

// =============================================================================
// CONNECT STORE MODAL
// =============================================================================
const ConnectStoreModal = ({ isOpen, onClose, userId }) => {
  const [shopDomain, setShopDomain] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = () => {
    if (!shopDomain.trim()) return;
    setLoading(true);
    const domain = shopDomain.includes('.myshopify.com') ? shopDomain : `${shopDomain}.myshopify.com`;
    window.location.href = `${API_BASE}?action=auth&shop=${encodeURIComponent(domain)}&userId=${userId}`;
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Connect Shopify Store</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Store Domain</label>
          <div className="flex">
            <input type="text" value={shopDomain} onChange={e => setShopDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleConnect()}
              placeholder="mystore"
              className="flex-1 px-3 py-2 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-sm" />
            <span className="px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-r-lg text-gray-500 text-sm">.myshopify.com</span>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm">Cancel</button>
          <button onClick={handleConnect} disabled={!shopDomain.trim() || loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}
            Connect Store
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// STORE SETTINGS MODAL
// =============================================================================
const StoreSettingsModal = ({ isOpen, onClose, store, userId, onSave }) => {
  const [settings, setSettings] = useState({
    shop_name: '', color: '#96bf48', is_primary: false,
    ad_platform: null, google_ads_account_id: null, meta_account_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [adAccounts, setAdAccounts] = useState({ google_ads: [], meta_ads: [] });
  const [campaigns, setCampaigns] = useState({ google_ads: [], meta_ads: [], selected: [] });
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);

  // Load settings + accounts when modal opens
  useEffect(() => {
    if (!isOpen || !store) return;
    setSettings({
      shop_name: store.shopName || '',
      color: store.color || '#96bf48',
      is_primary: store.isPrimary || false,
      ad_platform: store.adPlatform || null,
      google_ads_account_id: store.googleAdsAccountId || null,
      meta_account_id: store.metaAccountId || null,
    });
    loadAccounts();
    loadCampaigns();
  }, [isOpen, store]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await authFetch(`${API_BASE}?action=ad-accounts&userId=${userId}`);
      const data = await res.json();
      if (data.success) setAdAccounts({ google_ads: data.google_ads || [], meta_ads: data.meta_ads || [] });
    } catch {}
    setLoadingAccounts(false);
  };

  const loadCampaigns = async () => {
    if (!store?.id) return;
    try {
      const res = await authFetch(`${API_BASE}?action=campaigns&userId=${userId}&storeId=${store.id}`);
      const data = await res.json();
      if (data.success) setCampaigns({
        google_ads: data.google || [],
        meta_ads:   data.meta   || [],
        selected:   data.selected || store.selectedCampaigns || [],
      });
    } catch {}
  };

  const toggleCampaign = (id) => {
    setCampaigns(c => ({
      ...c,
      selected: c.selected.includes(id) ? c.selected.filter(x => x !== id) : [...c.selected, id],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch(`${API_BASE}?action=update-store`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId, storeId: store.id,
          updates: { ...settings, selected_campaigns: campaigns.selected },
        }),
      });
      onSave();
      onClose();
    } catch {}
    setSaving(false);
  };

  if (!isOpen || !store) return null;

  const colors = ['#96bf48','#3B82F6','#8B5CF6','#EF4444','#F59E0B','#10B981','#EC4899','#6366F1'];
  const allCampaigns = [
    ...campaigns.google_ads.map(c => ({ ...c, platform: 'Google Ads', type: 'Campaign' })),
    ...campaigns.meta_ads.map(c => ({ ...c, platform: 'Meta Ads', type: 'Campaign' })),
  ];

  // Has any real selectable items (exclude placeholder)
  const selectableItems = allCampaigns.filter(c => !c.disabled);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Store Settings</h3>
            <p className="text-sm text-gray-500">{store.shopDomain}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* Display name */}
          <div>
            <label className="block text-sm font-medium mb-1">Display Name</label>
            <input type="text" value={settings.shop_name} onChange={e => setSettings(s => ({ ...s, shop_name: e.target.value }))}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm" />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium mb-2">Store Color</label>
            <div className="flex gap-2 flex-wrap">
              {colors.map(c => (
                <button key={c} onClick={() => setSettings(s => ({ ...s, color: c }))}
                  className={`w-8 h-8 rounded-full border-2 ${settings.color === c ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {/* Primary store */}
          <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer">
            <input type="checkbox" checked={settings.is_primary} onChange={e => setSettings(s => ({ ...s, is_primary: e.target.checked }))} className="rounded" />
            <div>
              <p className="font-medium text-sm">Primary Store</p>
              <p className="text-xs text-gray-500">Show this store first on dashboard</p>
            </div>
          </label>

          {/* Ad platform */}
          <div>
            <label className="block text-sm font-medium mb-2">Ad Platform for ROAS</label>
            <div className="space-y-2">
              {[
                { value: null,          label: 'None (Manual Ad Spend)' },
                { value: 'google_ads',  label: 'Google Ads'             },
                { value: 'meta_ads',    label: 'Meta Ads (Facebook/Instagram)' },
                { value: 'both',        label: 'Both (Google + Meta)'   },
              ].map(({ value, label }) => (
                <label key={String(value)} className="flex items-center gap-3 p-3 border dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input type="radio" name="ad_platform" checked={settings.ad_platform === value}
                    onChange={() => setSettings(s => ({ ...s, ad_platform: value, google_ads_account_id: null, meta_account_id: null }))} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Google Ads account selector */}
          {(settings.ad_platform === 'google_ads' || settings.ad_platform === 'both') && (
            <div>
              <label className="block text-sm font-medium mb-1">Google Ads Account</label>
              {loadingAccounts ? (
                <p className="text-sm text-gray-400 flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Loading accounts...</p>
              ) : adAccounts.google_ads.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                  No Google Ads accounts connected. <a href="/google-ads" className="underline font-medium">Connect one here →</a>
                </div>
              ) : (
                <select value={settings.google_ads_account_id || ''} onChange={e => setSettings(s => ({ ...s, google_ads_account_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
                  <option value="">Select account...</option>
                  {adAccounts.google_ads.map(acc => <option key={acc.id} value={acc.id}>{acc.account_name || acc.account_id}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Meta account selector */}
          {(settings.ad_platform === 'meta_ads' || settings.ad_platform === 'both') && (
            <div>
              <label className="block text-sm font-medium mb-1">Meta Ads Account</label>
              {loadingAccounts ? (
                <p className="text-sm text-gray-400 flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Loading accounts...</p>
              ) : adAccounts.meta_ads.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                  No Meta Ads accounts connected. <a href="/meta-ads" className="underline font-medium">Connect one here →</a>
                </div>
              ) : (
                <select value={settings.meta_account_id || ''} onChange={e => setSettings(s => ({ ...s, meta_account_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
                  <option value="">Select account...</option>
                  {adAccounts.meta_ads.map(acc => <option key={acc.id} value={acc.id}>{acc.account_name || acc.account_id}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Campaign selector */}
          {settings.ad_platform && allCampaigns.length > 0 && (
            <div>
              <button onClick={() => setShowCampaigns(v => !v)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700">
                <span>
                  Filter Campaigns for ROAS
                  {campaigns.selected.length > 0
                    ? <span className="ml-2 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{campaigns.selected.length} selected</span>
                    : <span className="ml-2 text-xs text-gray-400">(all campaigns)</span>}
                </span>
                {showCampaigns ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showCampaigns && (
                <div className="border dark:border-gray-600 rounded-lg overflow-hidden mt-1 max-h-64 overflow-y-auto">
                  {campaigns.google_ads.length > 0 && (
                    <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                      Google Ads — Campaigns
                    </div>
                  )}
                  {campaigns.google_ads.map(c => (
                    <div key={c.id} onClick={() => toggleCampaign(c.id)}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b dark:border-gray-700 last:border-b-0">
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${campaigns.selected.includes(c.id) ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                        {campaigns.selected.includes(c.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <p className="text-sm truncate flex-1">{c.name}</p>
                      {c.status && <span className="text-xs text-gray-400 flex-shrink-0">{c.status}</span>}
                    </div>
                  ))}

                  {campaigns.meta_ads.length > 0 && (
                    <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                      Meta Ads — Campaigns
                    </div>
                  )}
                  {campaigns.meta_ads.map(c => (
                    c.disabled ? (
                      <div key={c.id} className="px-3 py-3 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 flex items-start gap-2">
                        <span className="flex-shrink-0 mt-0.5">⚠</span>
                        <span>{c.name}</span>
                      </div>
                    ) : (
                      <div key={c.id} onClick={() => toggleCampaign(c.id)}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b dark:border-gray-700 last:border-b-0">
                        <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${campaigns.selected.includes(c.id) ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                          {campaigns.selected.includes(c.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <p className="text-sm truncate flex-1">{c.name}</p>
                        {c.status && <span className={`text-xs flex-shrink-0 ${c.status === 'ACTIVE' ? 'text-green-500' : 'text-gray-400'}`}>{c.status}</span>}
                      </div>
                    )
                  ))}

                  {selectableItems.length > 0 && (
                    <div className="px-3 py-2 border-t dark:border-gray-700 flex gap-2">
                      <button onClick={() => setCampaigns(c => ({ ...c, selected: [] }))}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        Clear all
                      </button>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <button onClick={() => setCampaigns(c => ({ ...c, selected: selectableItems.map(s => s.id) }))}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        Select all
                      </button>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1.5">
                Meta campaigns are loaded from your <a href="/meta-ads" className="underline hover:text-primary">Meta Ads page</a>. Refresh them there if the list is empty.
                The spend values already include your exchange rate and tax settings.
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// STORES TAB
// =============================================================================
const StoresTab = ({ stores, onConnect, onSettings, onDisconnect }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <h2 className="text-lg font-semibold">Connected Stores</h2>
      <button onClick={onConnect} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
        <Plus className="w-4 h-4" /> Connect Store
      </button>
    </div>
    {stores.length === 0 ? (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700">
        <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="font-medium mb-2">No stores connected</h3>
        <p className="text-gray-500 mb-4 text-sm">Connect your first Shopify store to get started</p>
        <button onClick={onConnect} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Connect Shopify Store</button>
      </div>
    ) : (
      <div className="grid gap-4 md:grid-cols-2">
        {stores.map(store => (
          <div key={store.id} className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
            <div className="h-2" style={{ backgroundColor: store.color || '#96bf48' }} />
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{store.shopName}</h3>
                    {store.isPrimary && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Primary</span>}
                  </div>
                  <p className="text-sm text-gray-500">{store.shopDomain}</p>
                </div>
                <button onClick={() => onSettings(store)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <Settings className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm space-y-1.5">
                {[
                  ['Currency', store.currency || '—'],
                  ['Ad Platform', store.adPlatform === 'google_ads' ? 'Google Ads' : store.adPlatform === 'meta_ads' ? 'Meta Ads' : store.adPlatform === 'both' ? 'Google + Meta' : 'None'],
                  ['Last Synced', store.lastFetch ? new Date(store.lastFetch).toLocaleDateString() : 'Never'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-gray-500">{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t dark:border-gray-700">
                <a href={`https://${store.shopDomain}/admin`} target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
                  <ExternalLink className="w-3 h-3" /> Shopify Admin
                </a>
                <button onClick={() => onDisconnect(store.id)} className="px-3 py-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// =============================================================================
// PRODUCTS TAB
// =============================================================================
const ProductCostModal = ({ isOpen, onClose, product, userId, onSave }) => {
  const [costs, setCosts] = useState({ cost_of_goods: 0, shipping_cost: 0, packaging_cost: 0, fulfillment_cost: 0, other_costs: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) setCosts({
      cost_of_goods:    product.cost_of_goods    || 0,
      shipping_cost:    product.shipping_cost    || 0,
      packaging_cost:   product.packaging_cost   || 0,
      fulfillment_cost: product.fulfillment_cost || 0,
      other_costs:      product.other_costs      || 0,
    });
  }, [product]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch(`${API_BASE}?action=update-product`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, productId: product.id, costs }),
      });
      onSave();
      onClose();
    } catch {}
    setSaving(false);
  };

  if (!isOpen || !product) return null;
  const price = parseFloat(product.price || 0);
  const totalCost = Object.values(costs).reduce((s, v) => s + parseFloat(v || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{product.title}</h3>
            {product.variant_title && <p className="text-sm text-gray-500">{product.variant_title}</p>}
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {[
            { key: 'cost_of_goods',    label: 'Cost of Goods (COGS)' },
            { key: 'shipping_cost',    label: 'Shipping Cost'        },
            { key: 'packaging_cost',   label: 'Packaging Cost'       },
            { key: 'fulfillment_cost', label: 'Fulfillment Cost'     },
            { key: 'other_costs',      label: 'Other Costs'          },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-1">{label}</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input type="number" step="0.01" min="0" value={costs[key]}
                  onChange={e => setCosts(c => ({ ...c, [key]: e.target.value }))}
                  className="flex-1 px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm" />
              </div>
            </div>
          ))}
          <div className="pt-3 border-t dark:border-gray-700 space-y-1.5 text-sm">
            {[
              ['Sale Price', `$${price.toFixed(2)}`],
              ['Total Cost', `$${totalCost.toFixed(2)}`],
              ['Gross Profit', `$${(price - totalCost).toFixed(2)}`, price - totalCost >= 0 ? 'text-green-600' : 'text-red-600'],
            ].map(([k, v, cls]) => (
              <div key={k} className="flex justify-between">
                <span className="text-gray-500">{k}</span>
                <span className={cls || ''}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Costs
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductsTab = ({ storeId, userId, stores }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(storeId);

  useEffect(() => { if (selectedStoreId) loadProducts(); }, [selectedStoreId, page, search]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'products', userId, storeId: selectedStoreId, page: page.toString(), limit: '20', ...(search && { search }) });
      const res = await authFetch(`${API_BASE}?${params}`);
      const data = await res.json();
      if (data.success) { setProducts(data.products); setTotalPages(data.totalPages || 1); }
    } catch {}
    setLoading(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await authFetch(`${API_BASE}?action=sync-products`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, storeId: selectedStoreId }),
      });
      loadProducts();
    } catch {}
    setSyncing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Products</h2>
          {stores.length > 1 && (
            <select value={selectedStoreId} onChange={e => { setSelectedStoreId(e.target.value); setPage(1); }}
              className="px-3 py-1.5 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
              {stores.map(s => <option key={s.id} value={s.id}>{s.shopName}</option>)}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search products..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm" />
          </div>
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="font-medium mb-1">No products found</p>
          <p className="text-sm text-gray-500 mb-4">Sync your store to import products</p>
          <button onClick={handleSync} disabled={syncing} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Sync Products</button>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Product','SKU','Price','COGS','Margin','Stock','Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {products.map(p => {
                    const price = parseFloat(p.price || 0);
                    const cogs  = parseFloat(p.cost_of_goods || 0) + parseFloat(p.shipping_cost || 0) + parseFloat(p.packaging_cost || 0) + parseFloat(p.fulfillment_cost || 0) + parseFloat(p.other_costs || 0);
                    const margin = price > 0 ? ((price - cogs) / price * 100) : 0;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {p.image_url ? <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>}
                            <div className="min-w-0">
                              <p className="font-medium truncate max-w-[180px]">{p.title}</p>
                              {p.variant_title && <p className="text-xs text-gray-400 truncate">{p.variant_title}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{p.sku || '—'}</td>
                        <td className="px-4 py-3 font-medium">${price.toFixed(2)}</td>
                        <td className="px-4 py-3">${cogs.toFixed(2)}</td>
                        <td className="px-4 py-3"><span className={`font-medium ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{margin.toFixed(1)}%</span></td>
                        <td className="px-4 py-3">{p.inventory_quantity ?? '—'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => setSelectedProduct(p)} className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200">Set Costs</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border dark:border-gray-600 rounded text-sm disabled:opacity-50">← Prev</button>
              <span className="px-3 py-1 text-sm">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border dark:border-gray-600 rounded text-sm disabled:opacity-50">Next →</button>
            </div>
          )}
        </>
      )}

      <ProductCostModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} product={selectedProduct} userId={userId} onSave={loadProducts} />
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================
export default function ShopifyPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);


  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  // Re-fetch when user returns to this tab — store list may be stale
  useVisibilityRefresh(useCallback(() => { if (user?.id) loadData(); }, [user?.id]));

  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') loadData();
  }, [searchParams]);

  const loadData = async () => {
    try {
      const res = await authFetch(`${API_BASE}?action=stores&userId=${user.id}`);
      const data = await res.json();
      if (data.success) setStores(data.stores);
    } catch {}
    setLoading(false);
  };

  const handleDisconnect = async (storeId) => {
    if (!window.confirm('Disconnect this store?')) return;
    try {
      await authFetch(`${API_BASE}?action=disconnect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, storeId }),
      });
      loadData();
    } catch {}
  };

  if (loading) return <SkeletonShopify />;


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Shopify Stores</h1>
        <p className="text-muted-foreground">Connect and manage your Shopify stores. Analytics and reports are in the E-commerce section.</p>
      </div>

      {/* Connected stores — product cost management accessible from Ecommerce > Products */}
      <StoresTab stores={stores} onConnect={() => setShowConnectModal(true)} onSettings={s => setSelectedStore(s)} onDisconnect={handleDisconnect} />

      <ConnectStoreModal isOpen={showConnectModal} onClose={() => setShowConnectModal(false)} userId={user?.id} />
      <StoreSettingsModal isOpen={!!selectedStore} onClose={() => setSelectedStore(null)} store={selectedStore} userId={user?.id} onSave={loadData} />
    </div>
  );
}
